import { BroadcastChannel } from 'broadcast-channel';
import { getCookie } from 'cookies-next/client';
import { addDays } from 'date-fns';
import { type Params } from 'next/dist/server/request/params';
import { type NextRequest, type NextResponse } from 'next/server';
import { v4 as uuidv4 } from 'uuid';

import {
  sessionStorageSessionIdKey,
  localStorageSessionIdKey,
  localStorageSessionTimestampKey,
  requestTypes,
  type RequestType,
  sessionLandingPageKey,
  sessionProductViewsKey,
  newSessionBroadcastChannelName,
  newSessionBroadcastMessage,
  Platform,
  UNKNOWN_LANDING_PAGE,
} from './constants';
import { getRouteName } from './referers';

import {
  PERSISTENT_ID_KEY,
  PLATFORM_ID_KEY,
  RAKUTEN_COOKIE,
  RAKUTEN_LANDING_DATE_TIME,
  RAKUTEN_LANDING_ID,
  RAKUTEN_LANDING_UNIX_TIME,
  RAKUTEN_MERCHANT_ID,
} from '@/constants/cookies';
import {
  REFERRAL_EVENT_ENDPOINT,
  ACTIVITY_TRACKER_EVENT_ENDPOINT,
  ACTIVITY_TRACKER_SESSION_ENDPOINT,
} from '@/constants/endpoints';
import { H_SESSION_ID, X_PERSISTENT_ID } from '@/constants/headers';
import { DeviceType } from '@/modules/device/types';
import { isServerCheck } from '@/modules/env/isServerCheck';
import { http } from '@/modules/http';
import { constructDynamicRoute } from '@/modules/routing/constructDynamicRoute';
import {
  getSessionStorageItem,
  getMultipleLocalStorageItems,
  setMultipleSessionStorageItems,
  setMultipleLocalStorageItems,
  removeLocalStorageItem,
} from '@/modules/storage';
import { isInAppView } from '@/utils/isInAppView';

/**
 * Resolves the persistent id for this request and sets it on the *request*
 * headers. Reuses the existing cookie, or mints a new id when it's absent or
 * empty (first load, or privacy browsers / in-app webviews where the cookie
 * never persists).
 * Returns the resolved id and whether it was newly minted — callers persist it
 * to the response cookie (via `setPersistentIdMiddleware`) only when `isNew`.
 */
export function resolvePersistentId(req: NextRequest): {
  persistentId: string;
  isNew: boolean;
} {
  const existingPersistentId = req.cookies.get(PERSISTENT_ID_KEY)?.value;

  const persistentId = existingPersistentId || uuidv4();

  req.headers.set(X_PERSISTENT_ID, persistentId);

  return { persistentId, isNew: !existingPersistentId };
}

/**
 * Persists a freshly minted persistent id to the response cookie so subsequent
 * requests reuse it. Callers should only invoke this when a new id was minted
 * (see proxy.ts) — when the cookie already exists there is nothing to persist.
 *
 * Best-effort: privacy browsers / in-app webviews may drop the cookie, in which
 * case `resolvePersistentId` re-mints on the next request.
 */
export function setPersistentIdMiddleware(
  res: NextResponse,
  newPersistentId: string
) {
  res.cookies.set(PERSISTENT_ID_KEY, newPersistentId, {
    expires: addDays(new Date(), 365),
  });
}

export async function getPersistentId() {
  if (isServerCheck()) {
    const { headers, cookies } = await import('next/headers');
    return (
      (await headers()).get(X_PERSISTENT_ID) ||
      (await cookies()).get(PERSISTENT_ID_KEY)?.value
    );
  }
  const cookieId = getCookie(PERSISTENT_ID_KEY) as string;
  if (cookieId) {
    return cookieId;
  }
  // Fall back to the id the server rendered into the DOM when the cookie can't
  // be read
  return (
    document.querySelector<HTMLMetaElement>(`meta[name="${PERSISTENT_ID_KEY}"]`)
      ?.content || undefined
  );
}

export function getUrlParameters(url: string, urlParameter: string) {
  return new URL(url).searchParams.get(urlParameter);
}

export function getRakutenLandingTime(date: Date) {
  const minutes = date.getMinutes().toString().padStart(2, '0');
  const hours = date.getHours().toString().padStart(2, '0');
  const day = date.getDate().toString().padStart(2, '0');
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const year = date.getFullYear();

  return `${year}${month}${day}_${hours}${minutes}`;
}

export function setRakutenCookieMiddleware(
  req: NextRequest,
  res: NextResponse
) {
  const ranMID = getUrlParameters(req.url, 'ranMID');
  const ranSiteID = getUrlParameters(req.url, 'ranSiteID');

  if (ranMID && ranSiteID) {
    const now = new Date();

    const utcLandingDate = Date.UTC(
      now.getUTCFullYear(),
      now.getUTCMonth(),
      now.getUTCDate(),
      now.getUTCHours(),
      now.getUTCMinutes(),
      now.getUTCSeconds(),
      now.getUTCMilliseconds()
    );

    const utcDate = new Date(utcLandingDate);

    const landingUnixTimestamp = Math.floor(utcDate.valueOf() / 1000);
    const landingDateTime = getRakutenLandingTime(utcDate);
    const expiryDate = utcDate.setUTCDate(utcDate.getUTCDate() + 30);

    const rakutenCookie = `${RAKUTEN_MERCHANT_ID}:${ranMID}|${RAKUTEN_LANDING_DATE_TIME}:${landingDateTime}|${RAKUTEN_LANDING_UNIX_TIME}:${landingUnixTimestamp}|${RAKUTEN_LANDING_ID}:${ranSiteID}`;

    const cookieConfig = {
      expires: expiryDate,
      domain: '.depop.com',
      path: '/',
      secure: true,
      SameSite: 'lax',
    };

    res.cookies.set(RAKUTEN_COOKIE, rakutenCookie, cookieConfig);
  }
}

export function getEndpoint(requestType: RequestType) {
  switch (requestType) {
    case requestTypes.event:
      return ACTIVITY_TRACKER_EVENT_ENDPOINT;
    case requestTypes.session:
      return ACTIVITY_TRACKER_SESSION_ENDPOINT;
    case requestTypes.referral:
      return REFERRAL_EVENT_ENDPOINT;
    default:
      return null;
  }
}

interface Options {
  [key: string]: unknown;

  includeAuth?: boolean;
}

export function sendData(
  requestType: RequestType,
  data: unknown,
  options?: Options
) {
  const headers: Record<string, string> = {};

  // send the AT session id if it's there
  if (requestType === requestTypes.session) {
    const requestData = Array.isArray(data) ? data[0] : data;
    if (requestData.id) {
      headers[H_SESSION_ID] = requestData.id;
    }
  }

  const url = getEndpoint(requestType);

  if (url) {
    // TODO: align the OpenAPI spec so this can use typedHttp. sendData posts
    // polymorphic event payloads to one of several AT endpoints, so `data` is
    // unknown here and has no single per-endpoint request body type to satisfy
    // the openapi types.
    return http.post(url, data, {
      headers,
      withAuth: options?.includeAuth,
      keepalive: true,
    });
  }
  // @todo: Proper error handling
  return Promise.reject('There was a problem');
}

export function getCurrentSession() {
  const [localStorageSessionId, localStorageSessionTimestamp] =
    getMultipleLocalStorageItems([
      localStorageSessionIdKey,
      localStorageSessionTimestampKey,
    ]);
  return {
    sessionStorageSessionId: getSessionStorageItem(sessionStorageSessionIdKey),
    localStorageSessionId,
    localStorageSessionTimestamp,
  };
}

export function startNewSession() {
  const sessionId = uuidv4();
  const timestamp = new Date(Date.now()).toISOString();
  const pageName =
    getRouteName(window.location.pathname) || UNKNOWN_LANDING_PAGE;
  setMultipleSessionStorageItems([
    { key: sessionStorageSessionIdKey, value: sessionId },
  ]);
  setMultipleLocalStorageItems([
    {
      key: localStorageSessionIdKey,
      value: sessionId,
    },
    {
      key: localStorageSessionTimestampKey,
      value: timestamp,
    },
    { key: sessionLandingPageKey, value: pageName },
  ]);
  removeLocalStorageItem(sessionProductViewsKey);

  const channel = new BroadcastChannel(newSessionBroadcastChannelName);
  channel.postMessage(newSessionBroadcastMessage);
  channel.close();

  return {
    sessionId,
    timestamp,
  };
}

export function transformUserAgentToPlatformId(
  inAppView: boolean,
  userAgent?: string
): Platform {
  if (!inAppView || !userAgent) {
    return Platform.Web;
  }

  if (userAgent.includes('iPhone') || userAgent.includes('iPad')) {
    return Platform.iOS;
  } else if (userAgent.includes(DeviceType.ANDROID)) {
    return Platform.Android;
  }

  return Platform.Web;
}

export async function savePlatformToCookieMiddleware(
  req: NextRequest,
  res: NextResponse
) {
  const existingCookie = req.cookies.get(PLATFORM_ID_KEY)?.value;
  if (existingCookie) {
    return Promise.resolve();
  }
  const { headers } = await import('next/headers');

  const cookieConfig = {
    path: '/',
  };
  const inAppView = Boolean(await isInAppView());
  const userAgent = (await headers()).get('user-agent') || 'Unknown UA';

  const platformType = transformUserAgentToPlatformId(inAppView, userAgent);

  res.cookies.set(PLATFORM_ID_KEY, platformType, cookieConfig);
}

export async function getPlatform(): Promise<Platform> {
  if (isServerCheck()) {
    const { cookies } = await import('next/headers');
    return (await cookies()).get(PLATFORM_ID_KEY)?.value as Platform;
  }

  return (getCookie(PLATFORM_ID_KEY) as Platform) || Platform.Web;
}

export function getProductSlug(params: Params, segments: string[]) {
  const dynamicRoute = constructDynamicRoute(params, segments);

  if (dynamicRoute.startsWith('/products/[slug]') && params.slug) {
    return Array.isArray(params.slug) ? params.slug[0] : params.slug;
  }
}
